/**
 * Permission matching primitives.
 *
 * `substitute` fills `{key}` placeholders in a path template; `matchAll`
 * checks a list of requirements against the indexed Set built by
 * `usePermissionsIndex`.
 *
 * The frontend OpenAPI paths and the backend `resource_path` entries don't
 * always agree on placeholder names beyond. The OpenAPI spec
 * uses the short `google.api.http` aliases (e.g. `{automation}`), while the
 * permissions service emits the canonical proto field names (e.g.
 * `{automation_id}`). To make the Set lookup tolerant to that variance, both
 * sides are run through `normalizePlaceholders` — any remaining `{name}` is
 * collapsed to `{*}`. Org/workspace are always substituted with real IDs
 * before this runs, so they're unaffected.
 */
import type { PermissionRequirement } from './types';

export function substitute(
  template: string,
  params: Readonly<Record<string, string>>,
): string {
  return template.replace(/\{(\w+)\}/g, (match, key) => {
    if (params[key] !== undefined) return params[key];
    // Path templates use either {organization}/{workspace} or
    // {organization_id}/{workspace_id} for the same runtime IDs; accept the
    // short form from callers regardless of which the path uses.
    if (key === 'organization_id' && params.organization !== undefined) {
      return params.organization;
    }
    if (key === 'workspace_id' && params.workspace !== undefined) {
      return params.workspace;
    }
    return match;
  });
}

/**
 * Replaces every `{name}` placeholder with `{*}` so paths that name the same
 * runtime segment differently (e.g. `{automation}` vs `{automation_id}`)
 * compare equal.
 */
export function normalizePlaceholders(path: string): string {
  return path.replace(/\{[^}]+\}/g, '{*}');
}

/**
 * Returns true only when every requirement resolves to a `${method} ${path}`
 * key present in the index.
 *
 * The backend can grant a path org-wide by leaving `{workspace_id}`
 * unsubstituted in `resource_path`, which normalizes to `{*}` in the index.
 * So if the fully-scoped lookup misses, retry without `workspace` to fall
 * back to the wildcard form.
 */
export function matchAll(
  requirements: readonly PermissionRequirement[],
  params: Readonly<Record<string, string>>,
  index: Set<string>,
): boolean {
  return requirements.every(({ method, path }) => {
    const check = (p: Readonly<Record<string, string>>) => {
      const resolved = substitute(path, p);
      const trimmed = resolved.replace(/\/+$/, '') || resolved;
      return index.has(`${method} ${normalizePlaceholders(trimmed)}`);
    };
    if (check(params)) return true;
    const { workspace: _, ...withoutWorkspace } = params;
    return check(withoutWorkspace);
  });
}
